import { useSession } from '@/hooks';
import { usePrenuvoStore } from '@/stores/usePrenuvoStore';
import { Slot } from '@/types';
import {
  createHoldEventPayload,
  createSessionPayload,
  removeHoldEventPayload,
} from '@/utils/scanDates';
import { useShallow } from 'zustand/react/shallow';

export const useHandleSlots = () => {
  const { setMachineId } = usePrenuvoStore(
    useShallow((state) => ({
      setMachineId: state.scan.methods.setMachineId,
    })),
  );

  const {
    triggerCreateHoldEvent,
    triggerRemoveHoldEvent,
    triggerCreateSession,
    triggerClearSession,
  } = useSession();

  const addHoldEvent = async (slot: Slot, availableMachines: string[]) => {
    const currentMachineId = availableMachines.shift();

    if (!currentMachineId) {
      return;
    }

    const data: any = await triggerCreateHoldEvent(
      createHoldEventPayload(currentMachineId, slot.utcStart, slot.utcEnd),
    );

    if (data?.createHoldEvent?.status === 400) {
      return await addHoldEvent(slot, availableMachines);
    }

    setMachineId(currentMachineId);
    return { ...data, currentMachineId };
  };

  const isSessionTimestampValid = (errorsScanDetails: any, sessionStartTimestamp: any) => {
    return Object.keys(errorsScanDetails).length === 0 && !!sessionStartTimestamp;
  };

  const handleScanErrors = (errorsScanDetails: any, setError: any, scrollToElement: any) => {
    setError(
      'cannotProceedWithoutSelectingAScanDate',
      'Cannot proceed without selecting a scan date',
    );

    if (Object.keys(errorsScanDetails).includes('slot')) {
      scrollToElement('slot-error');
    }

    return false;
  };

  const isSameSlotAndMachine = (session: any, currentSlot: Slot | null, machineId: string) => {
    return (
      session?.timeSlot?.utcStart === currentSlot?.utcStart &&
      session?.timeSlot?.machineId === machineId
    );
  };

  const removeHoldEventAndClearSession = async (eventId: string, machineId: string) => {
    try {
      await triggerRemoveHoldEvent(removeHoldEventPayload(eventId, machineId));
      await triggerClearSession();
    } catch (error) {
      console.error('Error occurred while removing the hold event or clearing the session.', error);
    }
  };

  const handleHoldEventAndSession = async (
    currentSlot: any,
    availableMachines: any,
    setErrorModal: any,
    clearScan: any,
    setEventId: any,
    location: any,
    locationId: any,
    skuId: any,
    setSession: any,
    discount: any,
  ) => {
    try {
      const data = await addHoldEvent(currentSlot, availableMachines);

      if (!data || data?.createHoldEvent?.status === 400) {
        setErrorModal(
          'Error',
          data?.createHoldEvent?.message || 'No available slots for this spot',
          {
            onClose: clearScan,
          },
        );
        return false;
      }

      setEventId(data?.createHoldEvent?.eventID);

      if (!data?.currentMachineId) {
        clearScan();
        return false;
      }

      const sessionResponse = await triggerCreateSession(
        createSessionPayload(
          data?.createHoldEvent?.eventID,
          location?.code || locationId,
          Number(skuId),
          currentSlot,
          data?.currentMachineId,
          discount?.promoID,
        ),
      );

      const responseStatus = sessionResponse?.data?.createSession?.status;

      if (responseStatus === 200) {
        setSession(sessionResponse?.data?.createSession?.session);
        return true;
      }
    } catch (error) {
      console.error('Error creating hold event or session', error);
    }

    return false;
  };

  const handleSlots = async ({
    errorsScanDetails,
    setError,
    eventId,
    location,
    locationId,
    skuId,
    machineId,
    currentSlot,
    session,
    clearScan,
    setEventId,
    setErrorModal,
    setSession,
    scrollToElement,
    sessionStartTimestamp,
    discount,
  }: any) => {
    if (
      isSessionTimestampValid(errorsScanDetails, sessionStartTimestamp) &&
      isSameSlotAndMachine(session, currentSlot, machineId)
    ) {
      return true; // the user can proceed to the next page
    }

    if (Object.keys(errorsScanDetails).length !== 0) {
      return handleScanErrors(errorsScanDetails, setError, scrollToElement);
    }

    if (eventId) {
      await removeHoldEventAndClearSession(eventId, machineId);
    }

    const availableMachines = [...currentSlot.machines];

    return await handleHoldEventAndSession(
      currentSlot,
      availableMachines,
      setErrorModal,
      clearScan,
      setEventId,
      location,
      locationId,
      skuId,
      setSession,
      discount,
    );
  };

  return {
    handleSlots,
    isSessionTimestampValid,
    isSameSlotAndMachine,
    handleScanErrors,
    removeHoldEventAndClearSession,
    handleHoldEventAndSession,
  };
};
